// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing shipment lists with sorting and filtering capabilities.
 * Provides a convenient interface for fetching, sorting, and filtering shipment data.
 */
import { locationCompanyTypeSchema } from '@/constants/location-company-type';
import { sortDirectionSchema } from '@/constants/sort';
import {
  getShipments,
  shipmentSortPropertySchema,
} from '@/shipment/api/get-shipments';
import type { ShipmentFilter } from '@/shipment/api/get-shipments';
import { searchTermSchema } from '@/utilities/search-term';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Cache key for shipment queries
 */
export const shipmentsQueryKey = ['shipments'];

export function outboundShipmentCycleTimeReportQueryKey(filterString: string) {
  return [
    ...shipmentsQueryKey,
    'outbound-cycle-time-report',
    filterString,
  ] as const;
}

export function inboundShipmentCycleTimeReportQueryKey(filterString: string) {
  return [
    ...shipmentsQueryKey,
    'inbound-cycle-time-report',
    filterString,
  ] as const;
}

/**
 * Configuration options for the useShipments hook.
 * @property sort - Optional sorting configuration
 * @property filter - Optional filtering criteria
 */
interface UseShipmentsOptions {
  filter?: ShipmentFilter;
}

/**
 * React hook for fetching and managing shipment lists with sorting and filtering.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Sorting by shipment number, shipping cost, and status
 * - Filtering support via ShipmentFilter options
 * - Suspense mode for loading states
 *
 * @param options - Configuration object for the hook
 *
 * @returns Object containing:
 * - shipments: Sorted and filtered array of shipments
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useShipments(options: UseShipmentsOptions = {}) {
  // Set up React Query with suspense mode
  const { data, ...query } = useSuspenseQuery({
    queryKey: [...shipmentsQueryKey, options.filter],
    queryFn: () => getShipments(options.filter),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  /**
   * Sort the shipments array based on the current sort configuration.
   * Creates a new array to avoid mutating the original data.
   */
  const shipments = data.shipments;
  const resultCount = data.resultCount;
  const needsAttentionCount = data.needsAttentionCount;
  const quarantinedCount = data.quarantinedCount;
  return {
    shipments,
    resultCount,
    needsAttentionCount,
    quarantinedCount,
    ...query,
  };
}

/**
 * Search schema for shipment listing page.
 *
 * The from/to location filter parameters mirror the fulfillment listing and
 * support three independent dimensions per side: explicit location IDs,
 * customer IDs (which select all of that customer's locations), and
 * location company types (which select all manufacturer or customer
 * locations as a group).
 */
export const shipmentSearchListingSchema = z.object({
  query: searchTermSchema.catch(undefined),
  status: z
    .enum(['open', 'shipped', 'delivered', 'received', 'cancelled'])
    .optional()
    .catch(undefined),
  page: z.number().optional().catch(undefined),
  needsAttention: z.boolean().optional().catch(undefined),
  quarantined: z.boolean().optional().catch(undefined),
  sort: shipmentSortPropertySchema.optional().catch(undefined),
  direction: sortDirectionSchema.optional().catch(undefined),
  fromLocations: z.array(z.string()).optional(),
  fromCustomerLocations: z.array(z.string()).optional(),
  fromLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
  toLocations: z.array(z.string()).optional(),
  toCustomerLocations: z.array(z.string()).optional(),
  toLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
});

export type ShipmentSearchListing = z.infer<typeof shipmentSearchListingSchema>;
